import { ReadonlyVec2, mat4, quat, vec3, vec4 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { FrameBufferObject } from '~/engine/core/utils/FrameBufferObject';
import { ShaderProgram } from '~/engine/core/utils/ShaderProgram';
import { AdaptiveSphere } from '~/engine/data/common/customs/AdaptiveSphere';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ViewManager } from '~/engine/view/ViewManager';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { View } from '../../../view/View';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import {
    BrushDimensions,
    BrushDrawAction,
    BrushDrawActionState,
} from './BrushDrawAction';

/**
 * {@link AdaptiveBrushDrawActionState} is a {@link BrushDrawActionState} that stores the state of an active {@link AdaptiveBrushDrawAction}.\
 * It uses an {@link AdaptiveSphere} to render adaptive circles on the {@link drawObject}.
 */
class AdaptiveBrushDrawActionState extends BrushDrawActionState {
    /**
     * AdaptiveBrushDrawShader used to render adaptive circles on the {@link drawObject}.
     */
    private readonly shader: AdaptiveBrushDrawShader;

    /**
     * {@link FrameBufferObject} that is used to draw the data on the {@link drawObject}.\
     * This {@link FrameBufferObject} does not hold any render targets within.\
     * It is used via the {@link FrameBufferObject.createTextureLayer} method to draw the data on the {@link drawObject}.
     */
    private readonly fbo: FrameBufferObject;

    /**
     * {@link AdaptiveSphere} that is used to render the adaptive circles on the {@link drawObject}.\
     * It is updated in the {@link onMouseMove} method.\
     * And after that it is rendered on the {@link drawObject} using the AdaptiveBrushDrawShader.
     */
    private adaptiveSphere: AdaptiveSphere;

    /**
     * Constructor of the {@link AdaptiveBrushDrawActionState}.\
     * It creates the {@link AdaptiveSphere}, {@link FrameBufferObject} and AdaptiveBrushDrawShader that are used to render the adaptive circles on the {@link drawObject}.
     * @param view {@link PlaneView} on which the draw action is performed.
     * @param targets {@link Array} of {@link RegionOfInterest RegionsOfInterest} that are modified by the draw action.
     * @param imageObject {@link ImageObject} that defines the space in which the draw action is performed.\
     * This {@link ImageObject} is used as a data source to provide the data for the adaptive circles.
     * @param dimensions Number of dimensions of the {@link drawObject} and drawing. Either 2 or 3.\
     * Note that even if dimensions are 2, the {@link drawObject} is still 3D, but the thickness is equal to thickness of a single slice + margins.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1] at the beginning of the draw action.
     * @param brushSize Diameter of the brush in millimeters.
     * @param margin The distance of empty space around the brush in millimeters.\
     * This is also the distance at which values in the {@link drawObject} sdf turn from 0.5 to either 0 or 1.
     */
    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        imageObject: ImageObject,
        dimensions: BrushDimensions,
        coordinates: ReadonlyVec2,
        brushSize: number,
        erase: boolean
    ) {
        super(view, target, imageObject, dimensions, brushSize, erase);
        const patientCoordinates =
            this._planeView.clientToPatientCoordinates(coordinates);
        this.adaptiveSphere = new AdaptiveSphere(
            imageObject,
            patientCoordinates,
            brushSize,
            imageObject.grid.resX,
            vec4.fromValues(
                target.color[0],
                target.color[1],
                target.color[2],
                1.0
            ),
            this.margin
        );
        this.fbo = new FrameBufferObject(this.drawObject.planarPixelSize);
        this.shader = new AdaptiveBrushDrawShader();
        this.onMouseMove(coordinates, 0, view);
    }

    public get actionName(): string {
        return `Smart brush ${this._erase ? 'erase' : 'draw'}`;
    }

    /**
     * Method to inform the {@link AdaptiveBrushDrawActionState} about a mouse move event.\
     * If the {@link view} is not the same as {@link AdaptiveBrushDrawActionState.view this.view}, {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned.\
     * This method is somewhat complex, as it has to compute the intersection between the {@link drawObject} and the brush.\
     * And then iteratively render the adaptive circles on the {@link drawObject} using the AdaptiveBrushDrawShader.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1] at the time of the event.
     * @param view {@link View} on which the event occurred.
     * @returns An {@link EventHandlerResult} with type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if the {@link view} equals {@link StaticBrushDrawActionState.view this.view}
     * or {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if not.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: View
    ): EventHandlerResult {
        //add data to fbo
        //TODO: Brush draw action state: onMouseMove should also render cylinder in between start and end mouse position.
        //so things will get even more complex in the future.
        if (view !== this._planeView) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (!this.drawObject.texture) {
            throw new Error(
                `AdaptiveBrushDrawAction.onMouseMove while drawObject is not fully defined.`
            );
        }
        const patientCoordinates =
            this._planeView.clientToPatientCoordinates(coordinates);

        this.adaptiveSphere.setPosition(patientCoordinates);
        this.adaptiveSphere.bindReadTexture();
        if (this.adaptiveSphere) {
            this.fbo.bind();
            gl.viewport(0, 0, this.fbo.width, this.fbo.height);
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);
            this.shader.start();
            const translation = vec3.clone(this.drawObject.boundingBox.center);
            let i = 0;
            for (let z = 0; z < this.drawObject.grid.sizeZ; ++z) {
                this.fbo.createTextureLayer(this.drawObject.texture, i++);
                translation[2] = this.drawObject.grid.getNormalPosition(z);
                const transformation = mat4.fromRotationTranslationScale(
                    mat4.create(),
                    quat.create(),
                    translation,
                    vec3.scale(
                        vec3.create(),
                        this.drawObject.boundingBox.size,
                        0.5
                    )
                );
                const toProjection = mat4.invert(mat4.create(), transformation);
                const vao = this.adaptiveSphere.crossSection(toProjection);
                if (vao) {
                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
            this.shader.stop();
            this.fbo.unbind();
            gl.blendEquation(gl.FUNC_ADD);
            gl.disable(gl.BLEND);
        }
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(this.drawObject, 0, false)
        );
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * @override {@link BrushDrawActionState.clearMemory}
     * Method to deallocate the WebGL memory consumed by this {@link AdaptiveBrushDrawActionState}.\
     * It calls the {@link BrushDrawActionState.clearMemory} method and then deletes the {@link AdaptiveSphere}, AdaptiveBrushDrawShader and {@link FrameBufferObject}.
     * @returns True to indicate that the memory allocated by this {@link AdaptiveBrushDrawActionState} was successfully cleared.
     */
    public clearMemory() {
        super.clearMemory();
        this.adaptiveSphere.delete();
        this.shader.delete();
        this.fbo.delete();
    }
}

export class AdaptiveBrushDrawAction extends BrushDrawAction {
    public constructor(
        private readonly _structure: RegionOfInterest,
        private readonly _image: ImageObject,
        brushSize: number,
        erase: boolean,
        drawButton: number,
        cancelButton: number,
        modifierKeys?: ModifierKeys
    ) {
        super(brushSize, erase, drawButton, cancelButton, modifierKeys);
    }

    /**
     * @override Abstract method defined in {@link DrawAction.startAction}
     * Method called from {@link DrawAction}'s {@link DrawAction.onMousePress onMousePress}.
     * @throws An {@link Error} if {@link targetSpace} is not an {@link ImageObject}.
     * @param view {@link PlaneView} on which the event occured.
     * @param targets
     * @param targetSpace
     * @param canvasCoordinates
     * @returns
     */
    public startAction(
        view: PlaneView,
        canvasCoordinates: ReadonlyVec2
    ): AdaptiveBrushDrawActionState {
        const state = new AdaptiveBrushDrawActionState(
            view,
            this._structure,
            this._image,
            BrushDimensions.Brush2D,
            canvasCoordinates,
            this.brushSize,
            this._erase
        );
        this._state = state;
        return state;
    }
}

/**
 * Shader that is used to render texture content as-is: to copy the AdaptiveSphere texture to the draw object via a frame buffer.
 */
class AdaptiveBrushDrawShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoordinates = textureCoords;
    }
    `;

    /**
     * Fragment shader.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;

    out float color;

    uniform sampler3D sampler;

    void main(){
        color = texture(sampler, pass_textureCoordinates).r;
    }
    `;

    constructor() {
        super(
            AdaptiveBrushDrawShader.VERTEX_CONTENT,
            AdaptiveBrushDrawShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        super.storeUniformLocations();
    }
}
